import { Leaderboard } from "@/components/leaderboard/Leaderboard.js";
import { LeaderboardMetric } from "@rosette/ts/src/business/api/leaderboard/LeaderboardMetric.js";
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";
import { useLeaderboard } from "@/app/(api)/api/getLeaderboard/useLeaderboard.js";
import { LeaderboardType } from "@rosette/ts/src/business/api/leaderboard/LeaderboardType.js";
import type { LeaderboardPeriod } from "@rosette/ts/src/business/api/leaderboard/LeaderboardPeriod.js";
import type { LeaderboardGrouping } from "@rosette/ts/src/business/api/leaderboard/LeaderboardGrouping.js";
import { cn } from "@rosette/ts/src/util/cn.js";


interface LeaderboardWidgetProps {
    generateDisplayLeaderboards: (yearMonth: number) => LeaderboardType[];
    from?: number;
    limit?: number;
    displayDescAsTitle?: boolean;
}


export function LeaderboardWidget({
    generateDisplayLeaderboards, from, limit, displayDescAsTitle,
}: LeaderboardWidgetProps) {

    const [leaderboardType, setLeaderboardType] = useState<LeaderboardType | null>(null);
    const {
        leaderboard,
        leaderboardYourEntry,
        leaderboardLoading,
        leaderboardError,
    } = useLeaderboard(leaderboardType, from, limit);

    const displayLeaderboards: LeaderboardType[] = useMemo(
        () => {
            const types = generateDisplayLeaderboards(
                LeaderboardType.calculateYearMonth(new Date()),
            );
            if (types.length === 0)
                throw new Error("generateDisplayLeaderboards must return at least 1 leaderboard type");

            return types;
        },

        // We purposefully ignore generateDisplayLeaderboards changing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(() => {
        if (leaderboardType === null) {
            setLeaderboardType(displayLeaderboards[0]);
        }
    }, [displayLeaderboards, leaderboardType, setLeaderboardType]);

    return (
        <div className="flex flex-col">
            {leaderboardType && (
                <>
                    <div className="mb-2 flex flex-row items-center gap-3">
                        <AnimatePresence mode="wait">
                            <motion.div
                                className="grow"
                                key={leaderboardType.createID()}
                                {...fadeInOut({ duration: 0.2 })}>

                                <p className={cn(
                                    "m-0 w-full font-bold",
                                    displayDescAsTitle ? "text-strong" : "text-body",
                                )}>
                                    {leaderboardType.createDesc()}
                                </p>
                            </motion.div>
                        </AnimatePresence>

                        {displayLeaderboards.length > 1 && (
                            <button
                                className={cn(
                                    "cursor-pointer rounded-lg border border-panel bg-page px-2 py-1 text-base",
                                    "text-body hover:bg-surface hover:text-strong active:text-body",
                                )}
                                type="button"
                                onClick={() => {
                                    let next = displayLeaderboards[0];
                                    for (let index = 0; index < displayLeaderboards.length - 1; ++index) {
                                        if (displayLeaderboards[index] === leaderboardType) {
                                            next = displayLeaderboards[index + 1];
                                        }
                                    }
                                    setLeaderboardType(next);
                                }}>
                                Next
                            </button>
                        )}
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={leaderboardType.createID()}
                            {...fadeInOut({ duration: 0.2 })}>

                            <Leaderboard
                                leaderboard={leaderboard}
                                leaderboardYourEntry={leaderboardYourEntry}
                                leaderboardLoading={leaderboardLoading}
                                leaderboardError={leaderboardError}
                                formatValue={(_pos, _user, _prefs, value) => {
                                    const metric = leaderboardType.metric;
                                    if (metric === LeaderboardMetric.RATING) {
                                        return `${value.toFixed(0)}`;
                                    } else if (metric === LeaderboardMetric.ONLINE_WINS) {
                                        return `${value} Wins`;
                                    } else if (metric === LeaderboardMetric.PANDA_WINS) {
                                        return `${value} Wins`;
                                    } else {
                                        throw new Error(`Unknown metric: ${metric.getID()}`);
                                    }
                                }} />
                        </motion.div>
                    </AnimatePresence>
                </>
            )}
        </div>
    );
}


interface SimpleLeaderboardWidgetProps {
    className?: string;
    period: LeaderboardPeriod;
    grouping: LeaderboardGrouping;
    metric: LeaderboardMetric;
    from?: number;
    limit?: number;
}


export function SimpleLeaderboardWidget({
    className, period, grouping, metric, from, limit,
}: SimpleLeaderboardWidgetProps) {
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={`${period.getID()}__${grouping.getID()}__${metric.getID()}`}
                className={className}
                {...fadeInOut()}>

                <LeaderboardWidget
                    generateDisplayLeaderboards={yearMonth => [
                        new LeaderboardType(period, yearMonth, grouping, metric),
                    ]}
                    from={from}
                    limit={limit}
                    displayDescAsTitle={true} />
            </motion.div>
        </AnimatePresence>
    );
}
